import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../api/client';
import { StatusBadge } from '../../../components/shared/StatusBadge';
import { EntityLink } from '../../../components/shared/EntityLink';
import {
  Rocket, Search, FileText, Eye, CheckCircle2, XCircle,
  Clock, AlertCircle, Loader2, Package, Trash2, RefreshCw,
  ChevronLeft, ChevronRight, X, Send, Sparkles, Link,
  PenTool, Grid3X3, BarChart3, Filter, Check, Ban,
  TrendingUp, Binoculars, FolderTree, Plus,
  Play, Pause, Settings2, ChevronDown, ChevronUp,
  Globe, Star, Weight, ShoppingCart, Zap, Edit3, Layers
} from 'lucide-react';
import { DRAFT_STATUS_MAP, SITE_FLAGS } from '../shared';
import { ScoreBadge } from '../components/ScoreBadge';
import { useCan, Can } from '../../../lib/rbac';

function CompareModal({ draftId, onClose }: { draftId: number; onClose: () => void }) {
  const { t } = useTranslation();
  const canMoney = useCan()('global.view_money');
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/publisher/drafts/${draftId}/compare`)
      .then((r: any) => setData(r.data?.data || r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [draftId]);

  if (loading) return <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
    <Loader2 size={32} className="animate-spin text-white" />
  </div>;

  const amz = data?.amazon || {};
  const ml = data?.ml || {};

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-[var(--color-bg)] rounded-xl border border-[var(--color-border)] w-full max-w-5xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-[var(--color-border)]">
          <h3 className="font-bold text-[var(--color-text)]">{t('pub.compare')}</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-[var(--color-bg-tertiary)]"><X size={18} /></button>
        </div>
        <div className="grid grid-cols-2 gap-0">
          {/* Amazon side */}
          <div className="p-4 border-r border-[var(--color-border)]">
            <h4 className="text-sm font-bold text-orange-500 mb-3 flex items-center gap-2">
              <Package size={16} /> {t('pub.amazonOriginal')}
            </h4>
            {amz.images?.[0] && <img src={amz.images[0]} alt="" className="w-32 h-32 object-contain mx-auto mb-3 rounded" />}
            <div className="space-y-3 text-sm">
              <div><span className="text-[var(--color-text-tertiary)] text-xs block">Title</span><span className="text-[var(--color-text)]">{amz.title}</span></div>
              <div><span className="text-[var(--color-text-tertiary)] text-xs block">Brand</span><span className="text-[var(--color-text)]">{amz.brand}</span></div>
              <div><span className="text-[var(--color-text-tertiary)] text-xs block">Price</span><span className="text-[var(--color-text)] font-mono">{canMoney ? `$${Number(amz.price || 0).toFixed(2)}` : '\u2014'}</span></div>
              <div><span className="text-[var(--color-text-tertiary)] text-xs block">Category</span><span className="text-[var(--color-text)]">{amz.category}</span></div>
              <div><span className="text-[var(--color-text-tertiary)] text-xs block">Rating</span><span className="text-[var(--color-text)]">{amz.rating}</span></div>
              {amz.features?.length > 0 && (
                <div>
                  <span className="text-[var(--color-text-tertiary)] text-xs block mb-1">Features</span>
                  <ul className="list-disc pl-4 text-xs text-[var(--color-text-secondary)] space-y-1">
                    {amz.features.slice(0, 5).map((f: string, i: number) => <li key={i}>{f}</li>)}
                  </ul>
                </div>
              )}
            </div>
          </div>
          {/* ML side */}
          <div className="p-4">
            <h4 className="text-sm font-bold text-blue-500 mb-3 flex items-center gap-2">
              <Rocket size={16} /> {t('pub.mlListing')}
            </h4>
            {ml.images?.[0] && <img src={ml.images[0]} alt="" className="w-32 h-32 object-contain mx-auto mb-3 rounded" />}
            <div className="space-y-3 text-sm">
              <div><span className="text-[var(--color-text-tertiary)] text-xs block">{t('pub.titleSeo')}</span><span className="text-[var(--color-text)]">{ml.title}</span></div>
              <div><span className="text-[var(--color-text-tertiary)] text-xs block">{t('ai.category')}</span><span className="text-[var(--color-text)]">{ml.category}</span></div>
              <div className="flex items-center gap-2">
                <span className="text-[var(--color-text-tertiary)] text-xs">{t('common.price')}</span>
                <span className="text-[var(--color-text)] font-mono">{canMoney ? `$${Number(ml.price || 0).toFixed(2)}` : '\u2014'}</span>
                {ml.score != null && <ScoreBadge score={ml.score} />}
              </div>
              <div><span className="text-[var(--color-text-tertiary)] text-xs block">{t('pub.sites')}</span>
                <div className="flex gap-1 mt-1">{(ml.sites || []).map((s: string) => (
                  <span key={s} className="px-2 py-0.5 bg-blue-50 dark:bg-blue-900/20 text-blue-600 rounded text-xs">{SITE_FLAGS[s] || ''} {s}</span>
                ))}</div>
              </div>
              <div><span className="text-[var(--color-text-tertiary)] text-xs block mb-1">{t('pub.description')}</span>
                <div className="text-xs text-[var(--color-text-secondary)] whitespace-pre-line max-h-40 overflow-y-auto bg-[var(--color-bg-tertiary)] rounded p-2">{ml.description || '\u2014'}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SiteRetryPill({ draftId, siteId, status, errorDetail, onRetried }: {
  draftId: number; siteId: string; status: string; errorDetail?: any; onRetried: () => void;
}) {
  const [retrying, setRetrying] = useState(false);
  const can = useCan();
  const isError = status === 'error' || (!status?.match(/active|paused/));
  const errMsg = typeof errorDetail === 'string' ? errorDetail : errorDetail?.cause?.[0]?.message || errorDetail?.message || '';
  // Reintentar un site es publicar: sin la llave, la pastilla solo informa el error.
  if (isError && !can('publisher.publish')) return (
    <span className="px-2 py-0.5 rounded text-[10px] font-mono font-bold bg-red-100 text-red-700 border border-red-300 dark:bg-red-900/40 dark:text-red-300 dark:border-red-600" title={errMsg}>
      {siteId} !
    </span>
  );
  if (!isError) return (
    <span className="px-2 py-0.5 rounded text-[10px] font-mono font-bold bg-emerald-100 text-emerald-700 border border-emerald-300 dark:bg-emerald-900/40 dark:text-emerald-300 dark:border-emerald-600" title={siteId}>
      {siteId}
    </span>
  );
  return (
    <button
      onClick={async () => {
        setRetrying(true);
        try {
          await api.post(`/publisher/published/${draftId}/retry-site`, { site_id: siteId });
          onRetried();
        } catch { /* */ }
        setRetrying(false);
      }}
      disabled={retrying}
      className={`px-2 py-0.5 rounded text-[10px] font-mono font-bold border transition-colors ${
        retrying ? 'bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-900/40 dark:text-amber-300 dark:border-amber-600 animate-pulse'
        : 'bg-red-100 text-red-700 border-red-300 dark:bg-red-900/40 dark:text-red-300 dark:border-red-600 hover:bg-red-200 dark:hover:bg-red-800/50 cursor-pointer'
      }`}
      title={retrying ? 'Retrying...' : `Click to retry: ${errMsg}`}
    >
      {retrying ? `${siteId}...` : `${siteId} !`}
    </button>
  );
}

function DraftDetailModal({ draft: listDraft, onClose, onUpdate, sellerId }: {
  draft: any;
  onClose: () => void;
  onUpdate: () => void;
  sellerId: number;
}) {
  const { t } = useTranslation();
  const can = useCan();
  const canMoney = can('global.view_money');
  const canReview = can('publisher.drafts_review');
  const [draft, setDraft] = useState(listDraft);
  const [loading, setLoading] = useState(true);
  const [titleSeo, setTitleSeo] = useState(listDraft.title_seo || listDraft.title || '');
  const [description, setDescription] = useState(listDraft.description || '');
  // Trilingual previews: parent stays in English (CBT validates cause_id 5259);
  // sites get localized translations (ES for MLA/MLM/MLC/MCO, PT for MLB) via per-site PUT.
  const [descLang, setDescLang] = useState<'en' | 'es' | 'pt'>('en');
  const [price, setPrice] = useState(listDraft.price ? String(listDraft.price) : '');
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [showCompare, setShowCompare] = useState(false);
  const [siteItems, setSiteItems] = useState<any[]>([]);
  const [savingVoltage, setSavingVoltage] = useState<string | null>(null);
  const [voltageError, setVoltageError] = useState<string | null>(null);

  useEffect(() => {
    api.get<any>(`/publisher/drafts/${listDraft.id}`).then(res => {
      if (res.success && res.data) {
        setDraft(res.data);
        setTitleSeo(res.data.title_seo || res.data.title || '');
        setDescription(res.data.description || '');
        setPrice(res.data.price ? String(res.data.price) : '');
      }
      setLoading(false);
    }).catch(() => setLoading(false));
    // Load site items if published
    if (listDraft.status === 'published' && listDraft.id) {
      api.get<any>(`/publisher/published/${listDraft.id}/sites`).then(res => {
        if (res.success && res.data?.site_items) setSiteItems(res.data.site_items);
      }).catch(() => {});
    }
  }, [listDraft.id]);

  const hasChanges = titleSeo !== (draft.title_seo || draft.title || '') ||
    description !== (draft.description || '') ||
    price !== (draft.price ? String(draft.price) : '');

  const isPublished = draft.status === 'published';
  const validationDetails = draft.validation_details || {};
  const layerA = validationDetails.layer_a || {};
  const layerB = validationDetails.layer_b || {};
  const pics = draft.ml_payload?.pictures || [];
  const mainImg = pics[0]?.source || draft.product_image || draft.thumbnail;
  const payloadAttrs = draft.ml_payload?.attributes || [];
  const gtinAttr = payloadAttrs.find?.((a: any) => a.id === 'GTIN');
  const sites = draft.sites || draft.ml_payload?.sites_to_sell?.map((s: any) => s.site_id) || [];

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/publisher/drafts/${draft.id}`, { title_seo: titleSeo, description, price: price ? Number(price) : undefined, seller_id: sellerId });
      onUpdate();
    } catch { /* */ }
    setSaving(false);
  };

  // Voltaje resuelto a mano: el usuario elige y el borrador sale del motivo de revisión.
  const handleVoltage = async (voltage: 'dual' | '110' | '220' | 'not_electric') => {
    setSavingVoltage(voltage);
    try {
      const res = await api.put<any>(`/publisher/drafts/${draft.id}/review`, { voltage, seller_id: sellerId });
      if (res?.success && res.data) {
        setDraft((prev: any) => ({ ...prev, ...res.data }));
        if (res.data.title_seo) setTitleSeo(res.data.title_seo);
        if (res.data.description) setDescription(res.data.description);
        onUpdate();
      } else {
        setVoltageError(res?.error || 'No se pudo guardar');
      }
    } catch (e: any) {
      setVoltageError(e?.message || 'No se pudo guardar');
    }
    setSavingVoltage(null);
  };

  // ¿Hay un motivo de revisión por voltaje abierto? Con motivo, el selector va dentro del panel de
  // revisión; sin motivo pero con voltaje ya decidido, va en su propio recuadro (para corregir).
  const voltageReasonOpen = !!(draft.needs_review && draft.review_reasons?.some((r: string) => /voltage/i.test(r)));
  const voltageLabel = (v: string) =>
    v === '110' ? t('pub.voltage.only110')
      : v === '220' ? t('pub.voltage.only220')
      : v === 'dual' ? t('pub.voltage.dual')
      : v === 'not_electric' ? t('pub.voltage.notElectric')
      : t('pub.voltage.unknown');
  const voltageSelector = (
    <div className="mt-2 space-y-1.5">
      <div className="text-xs text-yellow-300">{t('pub.voltage.question')}</div>
      {draft.amazon_snapshot?.voltage_spec && (
        <div className="text-[11px] text-yellow-300/80">Amazon: <span className="font-mono">{draft.amazon_snapshot.voltage_spec}</span></div>
      )}
      <div className="flex flex-wrap gap-1.5">
        {(['dual', '110', '220', 'not_electric'] as const).map(v => (
          <button key={v} onClick={() => handleVoltage(v)} disabled={!canReview || savingVoltage !== null}
            className={`px-2.5 py-1 rounded text-xs hover:bg-yellow-700/60 disabled:opacity-40 ${draft.voltage === v && !voltageReasonOpen ? 'bg-yellow-600/60 text-white ring-1 ring-yellow-400' : 'bg-yellow-800/40 text-yellow-200'}`}>
            {savingVoltage === v ? '…' : voltageLabel(v)}
          </button>
        ))}
      </div>
      <div className="text-[11px] text-[var(--color-text-tertiary)]">{t('pub.voltage.help')}</div>
      {voltageError && <div className="text-[11px] text-red-400">{voltageError}</div>}
    </div>
  );

  const handleAction = async (action: 'approve' | 'reject') => {
    await api.post(`/publisher/drafts/${draft.id}/${action}`, { seller_id: sellerId });
    onUpdate(); onClose();
  };

  const handlePublish = async () => {
    setPublishing(true);
    try {
      await api.post('/publisher/drafts/publish', { ids: [draft.id], seller_id: sellerId });
      onUpdate(); onClose();
    } catch { /* */ }
    setPublishing(false);
  };

  const scoreColor = (s: number) => s >= 85 ? 'text-green-500' : s >= 60 ? 'text-yellow-500' : 'text-red-500';
  const scoreBg = (s: number) => s >= 85 ? 'bg-green-500' : s >= 60 ? 'bg-yellow-500' : 'bg-red-500';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-[var(--color-bg)] rounded-xl border border-[var(--color-border)] w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[var(--color-border)]">
          <div className="flex items-center gap-3">
            <h3 className="font-bold text-[var(--color-text)]">{t('pub.draftDetail')}</h3>
            <StatusBadge status={DRAFT_STATUS_MAP[draft.status] || 'unknown'} label={draft.status} />
            {draft.auto_published && <span className="px-1.5 py-0.5 rounded text-[10px] bg-purple-900/30 text-purple-400 border border-purple-700/30">Auto</span>}
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-[var(--color-bg-tertiary)]"><X size={18} /></button>
        </div>

        <div className="p-4 space-y-4">
          {/* ── Top: Image + Info Grid ── */}
          <div className="flex gap-4">
            <div className="flex-shrink-0 w-[180px]">
              {mainImg ? <img src={mainImg} alt="" className="w-[180px] h-[180px] rounded-lg object-cover" />
                : <div className="w-[180px] h-[180px] rounded-lg bg-[var(--color-bg-tertiary)] flex items-center justify-center"><Package size={40} className="text-[var(--color-text-tertiary)]" /></div>}
              {pics.length > 1 && (
                <div className="flex gap-1 mt-2 overflow-x-auto">
                  {pics.slice(0, 6).map((p: any, i: number) => <img key={i} src={p.source} alt="" className="w-8 h-8 rounded object-cover border border-[var(--color-border)]" />)}
                  {pics.length > 6 && <span className="w-8 h-8 flex items-center justify-center text-[10px] text-[var(--color-text-tertiary)]">+{pics.length - 6}</span>}
                </div>
              )}
              <div className="mt-2 text-center">{draft.asin && <EntityLink type="asin" value={draft.asin} />}</div>
              {canMoney && draft.amazon_price && <div className="mt-1 text-center text-xs text-[var(--color-text-secondary)]">Amazon: ${Number(draft.amazon_price).toFixed(2)}</div>}
            </div>

            <div className="flex-1 space-y-2">
              {/* 4-col info grid (V1 parity) */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <div className="bg-[var(--color-bg-tertiary)] rounded-lg p-2">
                  <div className="text-[10px] text-[var(--color-text-tertiary)]">Category</div>
                  <div className="text-xs font-medium text-[var(--color-text)]">{draft.ml_category_name || '—'}</div>
                  {draft.ml_category_id && <div className="text-[10px] text-[var(--color-text-tertiary)] font-mono">{draft.ml_category_id}</div>}
                </div>
                <div className="bg-[var(--color-bg-tertiary)] rounded-lg p-2">
                  <div className="text-[10px] text-[var(--color-text-tertiary)]">Confidence</div>
                  <div className="text-xs font-medium text-[var(--color-text)]">{draft.category_confidence || draft.ml_category_confidence || '—'}%</div>
                </div>
                <div className="bg-[var(--color-bg-tertiary)] rounded-lg p-2">
                  <div className="text-[10px] text-[var(--color-text-tertiary)]">Price</div>
                  <div className="text-xs font-medium text-green-400 font-mono">{canMoney ? `$${Number(draft.price || 0).toFixed(2)}` : '\u2014'}</div>
                </div>
                <div className={`bg-[var(--color-bg-tertiary)] rounded-lg p-2 ${draft.validation_score != null ? (draft.validation_score >= 85 ? 'ring-1 ring-green-700/40' : draft.validation_score >= 60 ? 'ring-1 ring-yellow-700/40' : 'ring-1 ring-red-700/40') : ''}`}>
                  <div className="text-[10px] text-[var(--color-text-tertiary)]">Score</div>
                  <div className={`text-xs font-bold font-mono ${draft.validation_score != null ? scoreColor(draft.validation_score) : 'text-[var(--color-text-tertiary)]'}`}>
                    {draft.validation_score ?? '—'}
                  </div>
                </div>
              </div>

              {/* Title SEO (editable) */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-[10px] text-[var(--color-text-tertiary)]">{t('pub.titleSeo')} ({titleSeo.length} chars)</label>
                  {gtinAttr?.value_name && <span className="text-[10px] font-mono text-[var(--color-text-tertiary)]">GTIN: {gtinAttr.value_name}</span>}
                </div>
                <textarea value={titleSeo} onChange={e => setTitleSeo(e.target.value)} rows={2} readOnly={!canReview}
                  className="w-full px-2 py-1.5 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded text-sm text-[var(--color-text)] resize-none" />
              </div>

              {/* Price (editable) */}
              <div className="flex items-center gap-3">
                {canMoney && (
                <div>
                  <label className="text-[10px] text-[var(--color-text-tertiary)]">{t('common.price')} (USD)</label>
                  <input type="number" value={price} onChange={e => setPrice(e.target.value)} step="0.01" readOnly={!canReview}
                    className="w-28 px-2 py-1.5 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded text-sm text-[var(--color-text)] font-mono" />
                </div>
                )}
                {/* Sites */}
                <div className="flex-1">
                  <label className="text-[10px] text-[var(--color-text-tertiary)]">Sites</label>
                  <div className="flex flex-wrap gap-1 mt-0.5">
                    {isPublished && siteItems.length > 0 ? (
                      siteItems.map((si: any) => (
                        <SiteRetryPill key={si.site_id} draftId={draft.id} siteId={si.site_id} status={si.status} errorDetail={si.error_detail}
                          onRetried={() => api.get<any>(`/publisher/published/${draft.id}/sites`).then(r => { if (r.success) setSiteItems(r.data.site_items || []); })} />
                      ))
                    ) : sites.length > 0 ? (
                      sites.map((s: string) => <span key={s} className="px-1.5 py-0.5 rounded text-[10px] font-mono font-bold bg-blue-100 text-blue-700 border border-blue-300 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-600">{s}</span>)
                    ) : <span className="text-[10px] text-[var(--color-text-tertiary)]">—</span>}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ── Validation Details (V1 parity: Layer A + Layer B) ── */}
          {(layerA.score != null || layerB.enhanced != null) && (
            <div className="bg-[var(--color-bg-tertiary)] rounded-lg p-3 space-y-2">
              <div className="text-xs font-semibold text-[var(--color-text-secondary)]">Validation Details</div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                {layerA.score != null && <div><span className="text-[var(--color-text-tertiary)]">Layer A:</span> <span className={`font-mono font-bold ${scoreColor(layerA.score)}`}>{layerA.score}</span></div>}
                {layerB.enhanced != null && (
                  <div>
                    <span className="text-[var(--color-text-tertiary)]">Layer B:</span>{' '}
                    {layerB.enhanced ? <><span className="font-mono font-bold text-green-400">Enhanced</span><span className="text-[var(--color-text-tertiary)] ml-1">(draft: {layerB.original_score || '—'})</span></> :
                      layerB.error ? <span className="text-red-400 text-[10px]">{layerB.error}</span> : <span className="text-[var(--color-text-tertiary)]">skipped</span>}
                  </div>
                )}
              </div>
              {layerB.improvements?.length > 0 && (
                <div className="space-y-0.5">{layerB.improvements.map((imp: string, i: number) => <div key={i} className="text-[10px] text-green-400">&#10004; {imp}</div>)}</div>
              )}
              {layerA.issues?.length > 0 && (
                <div className="space-y-0.5 mt-1">{layerA.issues.map((iss: any, i: number) => (
                  <div key={i} className={`text-[10px] font-mono ${iss.severity === 'critical' ? 'text-red-400' : 'text-yellow-400'}`}>
                    {iss.severity === 'critical' ? '\u2717' : '\u26A0'} {iss.check}: {iss.detail}
                  </div>
                ))}</div>
              )}
            </div>
          )}

          {/* ── Review Panel (voltage + sites override) ── */}
          {draft.needs_review && draft.review_reasons?.length > 0 && (
            <div className="bg-yellow-900/20 border border-yellow-800 rounded-lg p-3 space-y-2">
              <h4 className="text-xs font-medium text-yellow-400">Needs Review</h4>
              <div className="flex flex-wrap gap-1.5">
                {draft.review_reasons.map((r: string, i: number) => (
                  <span key={i} className="px-2 py-0.5 bg-yellow-800/40 text-yellow-300 rounded text-xs">{r}</span>
                ))}
              </div>
              {voltageReasonOpen && voltageSelector}
            </div>
          )}

          {/* ── Voltaje ya resuelto: el selector sigue a mano para corregir una elección equivocada ── */}
          {!voltageReasonOpen && draft.voltage && (
            <div className="bg-[var(--color-bg-tertiary)] border border-[var(--color-border)] rounded-lg p-3 space-y-1">
              <div className="text-xs text-[var(--color-text-secondary)]">
                {t('pub.voltage.current')}: <span className="font-medium text-[var(--color-text)]">{voltageLabel(draft.voltage)}</span>
                {' · '}{t('pub.voltage.change')}
              </div>
              {voltageSelector}
            </div>
          )}

          {/* ── Description (EN parent + ES/PT per-site translations) ── */}
          {(() => {
            const trans = (draft.translations && typeof draft.translations === 'string')
              ? (() => { try { return JSON.parse(draft.translations); } catch { return {}; } })()
              : (draft.translations || {});
            const esDesc: string = trans?.es?.description || '';
            const ptDesc: string = trans?.pt?.description || '';
            const hasEs = !!esDesc;
            const hasPt = !!ptDesc;
            const shown = descLang === 'es' ? esDesc : descLang === 'pt' ? ptDesc : description;
            return (
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <label className="text-[10px] text-[var(--color-text-tertiary)]">{t('pub.description')}</label>
                  <div className="flex rounded overflow-hidden border border-[var(--color-border)]">
                    <button onClick={() => setDescLang('en')} className={`px-2 py-0.5 text-[10px] ${descLang === 'en' ? 'bg-[var(--color-primary)] text-white' : 'text-[var(--color-text-tertiary)]'}`} title="Parent (CBT)">EN</button>
                    <button onClick={() => setDescLang('es')} disabled={!hasEs} className={`px-2 py-0.5 text-[10px] ${descLang === 'es' ? 'bg-[var(--color-primary)] text-white' : hasEs ? 'text-[var(--color-text-tertiary)]' : 'text-[var(--color-text-tertiary)] opacity-30'}`} title="MLA/MLM/MLC/MCO">ES</button>
                    <button onClick={() => setDescLang('pt')} disabled={!hasPt} className={`px-2 py-0.5 text-[10px] ${descLang === 'pt' ? 'bg-[var(--color-primary)] text-white' : hasPt ? 'text-[var(--color-text-tertiary)]' : 'text-[var(--color-text-tertiary)] opacity-30'}`} title="MLB">PT</button>
                  </div>
                  <span className="text-[10px] text-[var(--color-text-tertiary)] ml-auto">{shown.length} chars</span>
                </div>
                {descLang === 'en' ? (
                  <textarea value={description} onChange={e => setDescription(e.target.value)} rows={6} readOnly={!canReview}
                    className="w-full px-2 py-1.5 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded text-sm text-[var(--color-text)] resize-y" />
                ) : (
                  <div className="w-full px-2 py-1.5 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded text-sm text-[var(--color-text)] whitespace-pre-wrap max-h-48 overflow-y-auto">
                    {shown || `No ${descLang.toUpperCase()} translation available`}
                  </div>
                )}
              </div>
            );
          })()}

          {/* ── Score Bar ── */}
          {draft.validation_score != null && (
            <div>
              <div className="w-full h-1.5 bg-[var(--color-bg-tertiary)] rounded-full overflow-hidden">
                <div className={`h-full rounded-full ${scoreBg(draft.validation_score)}`} style={{ width: `${Math.min(100, draft.validation_score)}%` }} />
              </div>
            </div>
          )}

          {/* ── Attributes (from ml_payload) ── */}
          {payloadAttrs.length > 0 && (
            <details className="group">
              <summary className="text-xs font-medium text-[var(--color-text-secondary)] uppercase cursor-pointer select-none">
                {t('pub.attributes')} ({payloadAttrs.length})
              </summary>
              <div className="grid grid-cols-2 gap-1 mt-2 text-[11px]">
                {payloadAttrs.map((a: any, i: number) => (
                  <div key={i} className="flex justify-between gap-2 bg-[var(--color-bg-tertiary)] rounded px-2 py-0.5">
                    <span className="text-[var(--color-text-tertiary)] font-mono">{a.id}</span>
                    <span className="text-[var(--color-text)] text-right">{a.value_name || a.value_id || '—'}</span>
                  </div>
                ))}
              </div>
            </details>
          )}

          {/* ── Action Buttons ── */}
          <div className="flex items-center gap-2 pt-2 border-t border-[var(--color-border)] flex-wrap">
            {hasChanges && !isPublished && canReview && (
              <button onClick={handleSave} disabled={saving}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-[var(--color-primary)] text-white rounded text-sm font-medium hover:opacity-90 disabled:opacity-50">
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Save
              </button>
            )}
            <button onClick={() => setShowCompare(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 border border-[var(--color-border)] rounded text-sm text-[var(--color-text)] hover:bg-[var(--color-bg-tertiary)]">
              <Eye size={14} /> Compare
            </button>
            {!isPublished && (
              <>
                <Can cap="publisher.drafts_review">
                <button onClick={() => handleAction('approve')}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white rounded text-sm font-medium hover:bg-green-700">
                  <Check size={14} /> Approve
                </button>
                <button onClick={() => handleAction('reject')}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600/80 text-white rounded text-sm font-medium hover:bg-red-700">
                  <Ban size={14} /> Reject
                </button>
                </Can>
                <Can cap="publisher.publish">
                <button onClick={handlePublish} disabled={publishing}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
                  {publishing ? <Loader2 size={14} className="animate-spin" /> : <Rocket size={14} />} Publish
                </button>
                </Can>
              </>
            )}
            {isPublished && draft.ml_item_id && (
              <a href={`https://articulo.mercadolibre.com.ar/${draft.ml_item_id}`} target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-1.5 px-3 py-1.5 border border-[var(--color-border)] rounded text-sm text-blue-400 hover:bg-[var(--color-bg-tertiary)]">
                <Globe size={14} /> View on ML
              </a>
            )}
            <button onClick={onClose}
              className="ml-auto px-3 py-1.5 border border-[var(--color-border)] rounded text-sm text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-tertiary)]">
              Close
            </button>
          </div>
        </div>
      </div>
      {showCompare && <CompareModal draftId={draft.id} onClose={() => setShowCompare(false)} />}
    </div>
  );
}

export { DraftDetailModal };
